/**
 * Shared row-parsing helpers for media unit import adapters.
 *
 * All adapters call buildBaseRow() to produce the common fields of ParsedRow,
 * then merge type-specific overrides on top.
 *
 * Adapters MUST be pure (no DB, no I/O) per design.md constraint.
 */
import { parseCoordinateInput, toScalarValue, type MappedRow } from '../validate-row.js';
import type { ParsedRow, ValidationError } from './types.js';
import { normalizeAddress } from './normalize-address.js';
import { normalizeCityName } from '../../utils/normalize-city-name.js';

/** Trims a string, returning null if empty. Arrays are treated as null. */
export function trim(v: string | string[] | undefined): string | null {
  if (Array.isArray(v)) return null;
  const t = v?.trim();
  return t || null;
}

/** Parses a boolean from common Polish/English string representations. Arrays are treated as null. */
export function parseBool(value: string | string[] | undefined): boolean | null {
  if (!value || Array.isArray(value)) return null;
  const v = value.trim().toLowerCase();
  if (v === 'tak' || v === 'true' || v === '1') return true;
  if (v === 'nie' || v === 'false' || v === '0') return false;
  return null;
}

/** Parses a decimal string to a string decimal or null. Non-numeric or arrays return null. */
export function parseDecimal(value: string | string[] | undefined): string | null {
  if (!value || Array.isArray(value) || value.trim() === '') return null;
  const n = parseFloat(value.trim());
  return isNaN(n) ? null : String(n);
}

/** Parses decimal or DMS coordinate strings to decimal coordinate strings. */
export function parseCoordinate(value: string | string[] | undefined): string | null {
  if (!value || Array.isArray(value)) return null;
  return parseCoordinateInput(value);
}

/**
 * Splits a comma-separated 'Zdjęcia' column value into a trimmed array of
 * non-empty URLs. Returns undefined when the column is blank, absent, or an
 * array (arrays are not expected for this column).
 */
export function parsePhotoUrls(value: string | string[] | undefined): string[] | undefined {
  const scalar = toScalarValue(value);
  if (!scalar || scalar.trim() === '') return undefined;
  const urls = scalar
    .split(',')
    .map((u) => u.trim())
    .filter((u) => u !== '');
  return urls.length > 0 ? urls : undefined;
}

/** Parses an integer string. Returns null for non-integer, empty, or array input. */
export function parseInteger(value: string | string[] | undefined): number | null {
  if (!value || Array.isArray(value) || value.trim() === '') return null;
  const trimmed = value.trim();
  const n = parseInt(trimmed, 10);
  if (isNaN(n)) return null;
  if (String(n) !== trimmed) return null;
  return n;
}

/**
 * Validates that a required field (by DB column name) is non-empty.
 * Field name in the error is the Polish header label (for UI display).
 * Arrays (e.g. tags) are treated as absent for required-field checks.
 */
export function validateRequiredField(
  errors: ValidationError[],
  value: string | string[] | undefined,
  dbColumn: string,
  polishLabel: string,
): void {
  const scalar = toScalarValue(value);
  if (!scalar || scalar.trim() === '') {
    errors.push({
      field: polishLabel,
      message: `${polishLabel} jest wymagane`,
      code: 'field_required',
      params: { field: polishLabel },
      severity: 'error',
    });
  }
}

/**
 * Validates that an optional integer field, when present, is a positive
 * integer (> 0). A blank/absent value is not an error here — pair with
 * validateRequiredField separately if the field is also required.
 * Field name in the error is the Polish header label (for UI display).
 */
export function validatePositiveInteger(
  errors: ValidationError[],
  value: string | string[] | undefined,
  dbColumn: string,
  polishLabel: string,
): void {
  const scalar = toScalarValue(value);
  if (!scalar || scalar.trim() === '') return;
  const n = parseInteger(value);
  if (n === null || n <= 0) {
    errors.push({
      field: polishLabel,
      message: `${polishLabel} musi być liczbą całkowitą dodatnią`,
      code: 'field_not_positive_integer',
      params: { field: polishLabel },
      severity: 'error',
    });
  }
}

/**
 * Digital/screen fields default to null; only the dooh adapter overrides
 * them with real parsed values (dooh-import-template capability). Extracted
 * from buildBaseRow to keep it under the max-lines-per-function limit.
 */
function buildDigitalScreenDefaults(): Pick<
  ParsedRow,
  | 'digitalSpotDurationSeconds'
  | 'digitalLoopSize'
  | 'digitalScreenResolution'
  | 'digitalScreenHours'
  | 'digitalDailyEmissions'
  | 'digitalTotalCampaignEmissions'
  | 'digitalSpotPrice'
  | 'screensCount'
  | 'isOutdoor'
  | 'digitalScreenSizeInches'
> {
  return {
    digitalSpotDurationSeconds: null,
    digitalLoopSize: null,
    digitalScreenResolution: null,
    digitalScreenHours: null,
    digitalDailyEmissions: null,
    digitalTotalCampaignEmissions: null,
    digitalSpotPrice: null,
    screensCount: null,
    isOutdoor: null,
    digitalScreenSizeInches: null,
  };
}

/**
 * Builds the common ParsedRow fields shared by all adapter types.
 * Type-specific fields (e.g. muralElement3d for mural type) are merged by each adapter.
 */
export function buildBaseRow(row: MappedRow, mediaType: string): ParsedRow {
  const scalarOrEmpty = (v: string | string[] | undefined): string =>
    toScalarValue(v)?.trim() ?? '';
  return {
    // null = blank in XLSX → auto-allocate grw_id server-side when a supplier_number
    // is present; a row with both blank is rejected in validateRow (REQ-2.5, REQ-2.6).
    grwId: trim(row['grw_id']),
    supplierNumber: trim(row['supplier_number']),
    city: normalizeCityName(scalarOrEmpty(row['city'])),
    address: normalizeAddress(scalarOrEmpty(row['address'])),
    region: scalarOrEmpty(row['region']),
    county: trim(row['county']),
    agglomeration: trim(row['agglomeration']),
    latitude: parseCoordinate(row['latitude']),
    longitude: parseCoordinate(row['longitude']),
    widthM: parseDecimal(row['width_m']),
    heightM: parseDecimal(row['height_m']),
    dimensionNotes: trim(row['dimension_notes']),
    material: trim(row['material']),
    lighting: trim(row['lighting']),
    audienceEstimate: parseInteger(row['audience_estimate']),
    notes: trim(row['notes']),
    wfContactPerson: trim(row['wf_contact_person']),
    locationDescription: trim(row['location_description']),
    mediaType,
    videocardLink: trim(row['videocard_link']),
    roadNumber: trim(row['road_number']),
    muralElement3d: parseBool(row['mural_element_3d']),
    muralLedTape: parseBool(row['mural_led_tape']),
    muralNeonLed: parseBool(row['mural_neon_led']),
    muralFluoUv: parseBool(row['mural_fluo_uv']),
    rent: parseDecimal(row['rent']),
    exposureCost: parseDecimal(row['exposure_cost']),
    catalogPrice: parseDecimal(row['catalog_price']),
    discountedPrice: parseDecimal(row['discounted_price']),
    lastMinutePrice: parseDecimal(row['last_minute_price']),
    starterPrice: parseDecimal(row['starter_price']),
    productionCost: parseDecimal(row['production_cost']),
    installationCost: parseDecimal(row['installation_cost']),
    dismantlingCost: parseDecimal(row['dismantling_cost']),
    additionalCosts: parseDecimal(row['additional_costs']),
    clientProduction: parseDecimal(row['client_production']),
    clientInstallation: parseDecimal(row['client_installation']),
    clientDismantling: parseDecimal(row['client_dismantling']),
    clientAdditionalFees: parseDecimal(row['client_additional_fees']),
    muralEcoProduction: parseDecimal(row['mural_eco_production']),
    multiformat: trim(row['multiformat']),
    ...buildDigitalScreenDefaults(),
    photoUrls: parsePhotoUrls(row['photo_urls']),
  };
}

/**
 * validate() implementation for adapters that require only Materiał and Oświetlenie.
 * Shared by cityscroll and słup adapters which are otherwise identical.
 */
export function validateMaterialLighting(row: MappedRow): ValidationError[] {
  const errors: ValidationError[] = [];
  validateRequiredField(errors, row['material'], 'material', 'Materiał');
  validateRequiredField(errors, row['lighting'], 'lighting', 'Oświetlenie');
  return errors;
}

/**
 * validate() implementation for adapters that require Szerokość [m], Wysokość [m],
 * Materiał, and Oświetlenie. Shared by billboard, backlight, and konstrukcja adapters.
 */
export function validateWidthHeightMaterialLighting(row: MappedRow): ValidationError[] {
  const errors: ValidationError[] = [];
  validateRequiredField(errors, row['width_m'], 'width_m', 'Szerokość [m]');
  validateRequiredField(errors, row['height_m'], 'height_m', 'Wysokość [m]');
  validateRequiredField(errors, row['material'], 'material', 'Materiał');
  validateRequiredField(errors, row['lighting'], 'lighting', 'Oświetlenie');
  return errors;
}

/**
 * Common required columns shared by ALL adapter types per spec.
 * These are Polish header strings from NOSNIKI_HEADERS.
 * Note: 'Typ nosnika' is in the common set but adapters do NOT include it
 * in their requiredColumns since it's checked by the worker dispatcher
 * (and skipped when mediaTypeOverride is active).
 *
 * 'Województwo' (region) is NOT required — files omitting it get region/county
 * backfilled from coordinates via enrichLocationFields (worker), see
 * import-mapping-transforms proposal.md.
 */
export const COMMON_REQUIRED_COLUMNS = [
  'Miejscowość',
  'Adres',
  'Numer dostawcy',
  'Dostawca',
  'Format',
  'Współrzędne - szerokość',
  'Współrzędne - długość',
] as const;

/**
 * Common optional columns that most adapters accept but don't require.
 */
export const COMMON_OPTIONAL_COLUMNS = [
  'Województwo',
  'Powiat',
  'Aglomeracja',
  'Nr drogi (dla konstrukcji)',
  'Numer Grupy RW',
  'Link do karty video dla WF',
  'Szacowana liczba odbiorców',
  'Wymiary niestandardowe',
  'Osoba do kontaktu przy WF i muralach',
  'Opis lokalizacji',
  'Uwagi',
  'Czynsz',
  'Koszt ekspozycji',
  'Cena katalogowa',
  'Cena po rabacie',
  'Cena last minute',
  'RW na start',
  'Koszt produkcji',
  'Produkcja',
  'Koszt montażu',
  'Montaż',
  'Koszt demontażu',
  'Demontaż',
  'Koszty dodatkowe',
  'Opłaty dodatkowe',
  'Tag 1',
  'Tag 2',
  'Tag 3',
  'Tag 4',
  'Tag 5',
  'Zdjęcia',
] as const;
